import React, {
  FC, useCallback, useRef,
} from 'react';
import DatePicker, { CalendarContainer, ReactDatePicker, registerLocale } from 'react-datepicker';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import { isSameDay } from 'date-fns';

import useDateFnsLocale from 'src/common/hooks/useDateFnsLocale';
import { useMediaQueries } from 'src/common/providers/MediaQueryProvider';
import { DatepickerProps } from './types';
import Icon from '../Icon';
import DatepickerInput from '../DatepickerInput';
import getClassNames from '../../utils/getClassNames';

import 'react-datepicker/dist/react-datepicker.css';
import styles from './Datepicker.module.scss';

const noop = () => { };

const useDateFnsLocalization = (): string | undefined => {
  const dateFnsLocale = useDateFnsLocale();
  if (typeof dateFnsLocale?.code === 'string') {
    registerLocale(dateFnsLocale.code, dateFnsLocale);
  }
  return dateFnsLocale?.code;
};

const Datepicker: FC<DatepickerProps> = ({
  id,
  classNameModifier,
  disabledDates = [],
  enableSpecificDates,
  inline = false,
  scrollable = false,
  maxDate = null,
  minDate = null,
  focusSelectedMonth = false,
  placeholder = 'datepicker--placeholder-default',
  value = null,
  onChange,
  onBlur,
  onSelect,
  renderDayContents = null,
  dayClassName = null,
  onMonthChange = noop,
  onCalendarOpen = noop,
  onCalendarClose = noop,
  monthsShown: externalMonthsShown,
  highlightDates,
  startDate,
  endDate,
  selectsRange = false,
  openToDate = null,
  noBorder = false,
  expandedDaySize = false,
  /**
   * If set to true, the last empty week rows will be collapsed
   */
  collapseLastRowIfEmpty = false,
  /**
   * If set to true, the excluded dates (dates that are not in enableSpecificDates)
   * will be additionally styled with 'react-datepicker__day--excluded'
   */
  styleExcludeDates = false,
}) => {
  const locale = useDateFnsLocalization();
  const [t] = useTranslation('common');
  const { m: isMedium } = useMediaQueries();
  const datepickerRef = useRef<ReactDatePicker>(null);
  const modifiers = classNames(classNameModifier, {
    expandedDaySize, inline, noBorder, scrollable, collapseLastRowIfEmpty,
  });
  let monthsShown = isMedium ? 2 : 1;
  if (externalMonthsShown) {
    monthsShown = externalMonthsShown;
  }

  const closeDatepicker = useCallback(() => {
    if (datepickerRef.current) {
      datepickerRef.current.setOpen(false);
    }
  }, []);

  type DatePickerContainerProps = {
    children: React.ReactNode;
  };

  const showMobileView = !isMedium;

  const getDayClassName = useCallback((date: Date) => {
    // Get base className from prop function if it exists
    const baseClassName = dayClassName ? dayClassName(date) : '';

    // If styleExcludeDates is disabled, just return the base class
    if (!styleExcludeDates) {
      return baseClassName;
    }

    // Check if the date is excluded
    const isExcluded = !enableSpecificDates?.some(
      (allowedDate) => isSameDay(date, allowedDate),
    );

    // Add excluded classes if needed
    const excludedClasses = isExcluded ? 'react-datepicker__day--disabled react-datepicker__day--excluded' : '';

    // Return combined classes
    return baseClassName && excludedClasses ? `${baseClassName} ${excludedClasses}` : baseClassName || excludedClasses;
  }, [styleExcludeDates, dayClassName, enableSpecificDates]);

  const DatepickerContainer = useCallback(({ children }: DatePickerContainerProps) => (
    <div className={styles.calendarContainer}>
      <CalendarContainer className={classNames('react-datepicker', styles.calendar)}>
        <button
          type="button"
          onClick={closeDatepicker}
          className={styles.closeButton}
          aria-label={t('close-button-label')}
        >
          <Icon
            id="common/close"
            className={styles.icon}
          />
        </button>
        {children}
      </CalendarContainer>
    </div>
  ), [closeDatepicker, t]);
  const DatepickerMobileHeader = useCallback(({
    date,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }) => (
    <>
      <div className="react-datepicker__current-month">
        {date.toLocaleString(locale, {
          month: 'long',
          year: 'numeric',
        })}
      </div>
      <div className={styles.calendarNavigation}>
        <button
          aria-label={t('datepicker--previous-month')}
          type="button"
          onClick={decreaseMonth}
          disabled={prevMonthButtonDisabled}
          className={styles.button}
        >
          <Icon
            id="common/calendar-up-arrow"
            className={styles.icon}
          />
        </button>
        <button
          aria-label={t('datepicker--next-month')}
          type="button"
          onClick={increaseMonth}
          disabled={nextMonthButtonDisabled}
          className={styles.button}
        >
          <Icon
            id="common/calendar-down-arrow"
            className={styles.icon}
          />
        </button>
      </div>
    </>
  ), [locale, t]);

  if (inline) {
    return (
      <DatePicker
        id={id}
        locale={locale}
        inline
        focusSelectedMonth={focusSelectedMonth}
        fixedHeight
        selected={value}
        onChange={onChange}
        onBlur={onBlur}
        onSelect={onSelect}
        monthsShown={monthsShown}
        wrapperClassName={styles.root}
        calendarClassName={getClassNames(styles, 'calendar', modifiers)}
        dayClassName={getDayClassName}
        excludeDates={disabledDates}
        includeDates={enableSpecificDates}
        minDate={minDate}
        maxDate={maxDate}
        onMonthChange={onMonthChange}
        renderDayContents={renderDayContents}
        highlightDates={highlightDates}
        startDate={startDate}
        endDate={endDate}
        selectsRange={selectsRange}
        openToDate={openToDate}
      />
    );
  }

  return (
    <DatePicker
      id={id}
      portalId="__next"
      locale={locale}
      withPortal={!isMedium}
      isClearable
      fixedHeight
      focusSelectedMonth={focusSelectedMonth}
      ref={datepickerRef}
      calendarContainer={showMobileView ? DatepickerContainer : undefined}
      renderCustomHeader={showMobileView ? DatepickerMobileHeader : undefined}
      monthsShown={monthsShown}
      customInput={<DatepickerInput />}
      renderDayContents={renderDayContents}
      wrapperClassName={styles.root}
      className={classNameModifier} // This gets passed to customInput
      calendarClassName={getClassNames(styles, 'calendar', modifiers)}
      placeholderText={t(placeholder)}
      selected={value}
      minDate={minDate}
      maxDate={maxDate}
      excludeDates={disabledDates}
      onChange={onChange}
      onBlur={onBlur}
      onCalendarOpen={onCalendarOpen}
      onCalendarClose={onCalendarClose}
      onSelect={onSelect}
      dayClassName={getDayClassName}
      popperClassName={styles.popper}
      popperPlacement="bottom"
      startDate={startDate}
      endDate={endDate}
      selectsRange={selectsRange}
      popperModifiers={[
        {
          name: 'arrow',
          options: {
            padding: 21,
          },
        },
        {
          name: 'offset',
          options: {
            offset: ({ reference }) => {
              if (isMedium) {
                // This accounts for added margin on customInput
                return classNameModifier === 'searchFilter' ? [0, -30] : [0, 0];
              }
              return [0, -(reference.y + reference.height)];
            },
          },
        },
      ]}
    />
  );
};

export default Datepicker;
